import { syncMachineService } from '@/services/sync-state';
import { apiFetch } from '@/lib/api-fetch';
import { downloadVaultThunk } from './thunks/download-vault-thunk';
import { uploadVaultThunk, SyncConflictError, VaultDeletedError } from './thunks/upload-vault-thunk';
import { readVaultThunk } from './thunks/read-vault-thunk';
import { vaultDeleted } from './vault-slice';
import cryptographerService from '@/services/cryptographer-service';
import { getVaultMetadata, storeDownloadedVault } from '@/utils/vault-storage';
import type { AppDispatch } from './store';

// ============================================================================
// Debounce / single-flight guards
// ============================================================================

/** Pending debounce timers, keyed by local vault ID */
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Vault IDs for whom a cycle is currently executing */
const inFlightIds = new Set<string>();

/** Debounce window in ms — collapses rapid edits into a single cycle */
const DEBOUNCE_MS = 500;

// ============================================================================
// Public API
// ============================================================================

/**
 * Schedule a sync cycle for the given vault after a short debounce.
 *
 * Multiple calls within DEBOUNCE_MS are collapsed into a single cycle.
 * If a cycle is already in flight when the timer fires, the schedule is
 * pushed back by another DEBOUNCE_MS so as not to run two cycles in parallel.
 *
 * Call this from:
 *   - sync-middleware after `machine.markDirty()`
 *   - useSyncPauseResume when the device comes back online
 */
export function scheduleSync(vaultId: string, dispatch: AppDispatch): void {
  const existing = pendingTimers.get(vaultId);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(async () => {
    pendingTimers.delete(vaultId);

    if (inFlightIds.has(vaultId)) {
      // Cycle running — re-schedule so we don't lose the intent
      scheduleSync(vaultId, dispatch);
      return;
    }

    inFlightIds.add(vaultId);
    try {
      await runSyncCycle(vaultId, dispatch);
    } finally {
      inFlightIds.delete(vaultId);
    }
  }, DEBOUNCE_MS);

  pendingTimers.set(vaultId, timer);
}

/**
 * Immediately run one sync cycle for the given vault.
 *
 * Drives the machine from `idle` or `dirty` through:
 *   check → HEAD → fetch | upload | upToDate | conflict
 *
 * All machine transitions are made here; callers do not need to interact
 * with the machine directly.
 *
 * Prefer `scheduleSync` over calling this directly — it provides debouncing
 * and single-flight protection.
 */
export async function runSyncCycle(vaultId: string, dispatch: AppDispatch): Promise<void> {
  const machine = syncMachineService.get(vaultId);

  if (!machine) {
    console.warn(`[SyncOrchestrator] No machine found for vault ${vaultId} — skipping cycle`);
    return;
  }

  const currentStatus = machine.status;

  // Only run from stable states that imply "ready to sync"
  if (currentStatus !== 'idle' && currentStatus !== 'dirty') {
    console.log(`[SyncOrchestrator] Skipping cycle for ${vaultId} — state is "${currentStatus}"`);
    return;
  }

  const wasDirty = currentStatus === 'dirty';

  // --- Transition to Checking -----------------------------------------------
  const checkingState = machine.check();
  const { localId, serverId, serverLastModified: knownServerLM } = checkingState;

  // --- HEAD request to compare Last-Modified --------------------------------
  try {
    const headResponse = await apiFetch(`/api/vaults/${serverId}`, { method: 'HEAD' });

    // 404 — server vault is gone → drop to LocalOnly + notify
    if (headResponse.status === 404) {
      machine.fail('check', 'Vault not found on server (404)');
      machine.recoverToLocalOnly();
      dispatch(vaultDeleted({ message: 'Vault has been deleted on the server', vaultId: localId }));
      console.warn(`[SyncOrchestrator] Vault ${vaultId} deleted on server — transitioned to LocalOnly`);
      return;
    }

    if (!headResponse.ok) {
      throw new Error(`HEAD /api/vaults/${serverId} returned ${headResponse.status}`);
    }

    const serverCurrentLM = headResponse.headers.get('Last-Modified') ?? '';
    const serverHasChanged = serverCurrentLM !== '' && serverCurrentLM !== knownServerLM;

    // --- Conflict: both sides changed ---------------------------------------
    if (serverHasChanged && wasDirty) {
      const meta = getVaultMetadata(localId);
      machine.conflict({
        localChangedAt: meta?.modifiedAt ?? new Date().toISOString(),
        serverChangedAt: serverCurrentLM,
      });
      console.log(`[SyncOrchestrator] Conflict detected for vault ${vaultId}`);
      return;
    }

    // --- Fetch: server is newer, local was idle -----------------------------
    if (serverHasChanged && !wasDirty) {
      machine.fetch();
      try {
        const { data, lastModified: newLM } = await dispatch(
          downloadVaultThunk({ vaultId: serverId })
        ).unwrap();

        const effectiveLM = newLM ?? serverCurrentLM;

        storeDownloadedVault({
          vaultId: localId,
          encryptedBlob: data,
          enableSync: true,
          lastModified: effectiveLM,
          syncId: serverId,
        });

        // Reload decrypted vault in memory if it is currently open
        if (cryptographerService.hasCryptographer(localId)) {
          await dispatch(readVaultThunk(localId)).unwrap();
        }

        machine.fetched({
          serverLastModified: effectiveLM,
          lastSyncDate: new Date().toISOString(),
        });
        console.log(`[SyncOrchestrator] Downloaded update for vault ${vaultId}`);
      } catch (fetchError) {
        _handleFetchError(machine, vaultId, 'fetch', fetchError, dispatch);
      }
      return;
    }

    // --- Up to date: nothing changed on either side -------------------------
    if (!serverHasChanged && !wasDirty) {
      machine.upToDate({
        lastSyncDate: new Date().toISOString(),
        serverLastModified: serverCurrentLM || knownServerLM,
      });
      console.log(`[SyncOrchestrator] Vault ${vaultId} is up to date`);
      return;
    }

    // --- Upload: only local has changed ------------------------------------
    machine.upload();
    try {
      const { lastModified: newLM } = await dispatch(
        uploadVaultThunk({ vaultId: localId })
      ).unwrap();

      machine.uploaded({
        serverLastModified: newLM,
        lastSyncDate: new Date().toISOString(),
      });
      console.log(`[SyncOrchestrator] Uploaded vault ${vaultId}`);
    } catch (uploadError) {
      _handleUploadError(machine, vaultId, uploadError, dispatch);
    }
  } catch (outerError) {
    // Outer-level failure (network down during HEAD, etc.)
    _handleFetchError(machine, vaultId, 'check', outerError, dispatch);
  }
}

// ============================================================================
// Error helpers
// ============================================================================

function _handleUploadError(
  machine: ReturnType<typeof syncMachineService.getOrCreate>,
  vaultId: string,
  error: unknown,
  dispatch: AppDispatch,
): void {
  if (error instanceof SyncConflictError) {
    // Late conflict: server changed between our check and upload (race)
    const meta = getVaultMetadata(vaultId);
    machine.uploadConflicted({
      localChangedAt: meta?.modifiedAt ?? new Date().toISOString(),
      serverChangedAt: error.serverLastModified ?? new Date().toISOString(),
    });
    console.warn(`[SyncOrchestrator] Late conflict for vault ${vaultId}`);
  } else if (error instanceof VaultDeletedError) {
    machine.fail('upload', error.message);
    machine.recoverToLocalOnly();
    dispatch(vaultDeleted({ message: error.message, vaultId }));
    console.warn(`[SyncOrchestrator] Vault ${vaultId} deleted on server during upload`);
  } else if (_isNetworkError(error)) {
    try { machine.pause(); } catch { /* machine may have already transitioned */ }
    console.warn(`[SyncOrchestrator] Network error during upload — vault ${vaultId} paused`);
  } else {
    try { machine.fail('upload', (error as Error).message); } catch { /* ignore */ }
    console.error(`[SyncOrchestrator] Upload failed for vault ${vaultId}:`, error);
  }
}

function _handleFetchError(
  machine: ReturnType<typeof syncMachineService.getOrCreate>,
  vaultId: string,
  operation: 'check' | 'fetch',
  error: unknown,
  _dispatch: AppDispatch,
): void {
  if (_isNetworkError(error)) {
    try { machine.pause(); } catch { /* already paused or not pausable */ }
    console.warn(`[SyncOrchestrator] Network error during ${operation} — vault ${vaultId} paused`);
  } else {
    try { machine.fail(operation, (error as Error).message); } catch { /* already errored */ }
    console.error(`[SyncOrchestrator] ${operation} failed for vault ${vaultId}:`, error);
  }
}

function _isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch'))
  );
}
